import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { View } from "@-/com.npmjs.react-native";
import runes from "@-/com.npmjs.runes2";
import { hasMinLength } from "@-/com.npmjs.ts-array-length";
import { useEffect, useMemo } from "react";
import { Skeleton } from "@-/dev.improve.haniverse.components.ui.skeleton";
import {
  MnemonicLayoutSkeleton,
  ReferenceLayoutSkeleton,
  WordLayoutSkeleton,
} from "@-/dev.improve.haniverse.components.ui.suspense-page";
import { ToolbarActionsLane } from "@-/dev.improve.haniverse.components.ui.toolbar.button.icon";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import {
  PrimaryControlsStripBackControl,
  usePrimaryControlsStripBackGate,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.back";
import { PrimaryControlsStripTitle } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.title";
import {
  PrimaryControlsStripToolbar,
  useNavToolbarPublisher,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";
import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";

/**
 * The toolbar row while the slices load: the real back control (it needs no data, and a slow
 * load must stay escapable) beside a placeholder where the view toggle and More menu will sit.
 * Publishes the same slots as `MorphemePanelsActionsToolbar`, so nothing shifts when it swaps in.
 * A word has no toggle or menu (see `MorphemePanelsWordView`), so it gets the back control alone.
 */
function MorphemePanelsToolbarPlaceholder({ isWord }: { isWord: boolean }) {
  const { diameter, height } = useToolbarGridMetrics();
  const publish = useNavToolbarPublisher();
  const isFocused = useIsFocused();
  const backGate = usePrimaryControlsStripBackGate({ appShellPlacement: "toolbar" });
  const slots = useMemo(
    () => ({
      actions: null,
      leading:
        backGate === null ? null : (
          <PrimaryControlsStripBackControl gate={backGate} layout="app-shell" />
        ),
      panels: isWord ? null : (
        <View className="flex-row items-center gap-1" pointerEvents="none">
          <View style={{ width: diameter }} />
          <Skeleton className="w-40 rounded-full" style={{ height: diameter }} />
          <Skeleton className="rounded-full" style={{ height: diameter, width: diameter }} />
        </View>
      ),
    }),
    [backGate, diameter, isWord],
  );

  useEffect(() => {
    if (publish === null || !isFocused) {
      return;
    }
    return publish(slots);
  }, [isFocused, publish, slots]);

  if (publish !== null) {
    // oxlint-disable-next-line haniverse/no-return-null -- render-into-host: the slots above are published into the nav toolbar host, so nothing renders in place.
    return null;
  }

  return (
    <PrimaryControlsStripToolbar
      className="relative"
      height={height}
      stripFade={false}
      surface="page"
    >
      <ToolbarActionsLane>
        <View className="mr-2 shrink-0 justify-center" style={{ height: diameter }}>
          {slots.leading}
        </View>
      </ToolbarActionsLane>
      <View
        className="absolute inset-0 z-10 flex-row items-start justify-center pt-2.25"
        pointerEvents="box-none"
      >
        {slots.panels}
      </View>
    </PrimaryControlsStripToolbar>
  );
}

/**
 * The morpheme page before its slices arrive. Everything derivable from the route renders for
 * real: the header glyph is just the route's character, so it appears with the transition instead
 * of popping in after the data. Only the data-backed panels are skeletons.
 */
export function MorphemePanelsLayoutSkeleton({
  morpheme,
  view,
}: {
  morpheme: string;
  view: "info" | "mnemonics";
}) {
  const getGraphemeGlyph = useGetGraphemeGlyph();
  const isWord = hasMinLength(runes(morpheme), 2);
  return (
    <>
      <PrimaryControlsStripTitle>
        <GraphemeFontGlyph
          className="text-center text-[36px] leading-12.5 text-foreground"
          glyph={getGraphemeGlyph(morpheme)}
        />
      </PrimaryControlsStripTitle>
      <MorphemePanelsToolbarPlaceholder isWord={isWord} />
      {isWord ? (
        <WordLayoutSkeleton />
      ) : view === "mnemonics" ? (
        <MnemonicLayoutSkeleton />
      ) : (
        <ReferenceLayoutSkeleton />
      )}
    </>
  );
}
